import type { JSX, KeyboardEvent } from "react";
import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import type { SegmentDefinition } from "@adlywise/shared";
import DateRangePicker, {
  type DateRange,
} from "@/components/common/DateRangePicker";
import {
  BUCKETS,
  ColumnsSection,
  effectiveSortBy,
  GroupingsSection,
  MetricsSection,
  Segmented,
  SOURCES,
  SortSection,
  selectedMetricsCount,
  withBucket,
  withSource,
  type AnalyticsConfig,
} from "./ConfigurePanel";
import { AnalyticsFiltersSection } from "./AnalyticsFiltersSection";
import { CollapsibleSection } from "./CollapsibleSection";

// The analytics report's configuration, in a drawer from the right (where the
// app's other drawers open), half the viewport wide (full width on small
// screens). Top to bottom:
//   1. Time frame — date range (with its presets) + time bucket
//   2. Source     — ad requests / ad responses
//   3. Metrics    — collapsible
//   4. Group by   — collapsible
//   5. Filters    — collapsible; any property, grouped or not
//   6. Sort by    — collapsible; Time plus groupings
//   7. Columns    — collapsed by default; column order within each block
// then the run / cancel button. Every change goes straight to the page's state;
// nothing is fetched until the report is run.
//
// A modal dialog: overlay, close button, Escape; focus moves into it on open,
// Tab cycles inside it, and the page puts focus back on Configure on close.
// The panel is NOT slid in with a transform: a transformed ancestor would
// become the containing block of the date picker's `position: fixed` popover
// and misplace it.

type AnalyticsConfigDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  config: AnalyticsConfig;
  onConfigChange: (next: AnalyticsConfig) => void;
  range: DateRange;
  onRangeChange: (next: DateRange) => void;
  filter: SegmentDefinition;
  onFilterChange: (next: SegmentDefinition) => void;
  runReportButtonLabel: string;
  isRequestCancelable: boolean;
  isRunReportButtonDisabled: boolean;
  onRunReportButtonClick: () => void;
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const FieldLabel = ({ children }: { children: string }): JSX.Element => (
  <span className="text-[11px] font-bold uppercase tracking-wide text-(--text-muted)">
    {children}
  </span>
);

export const AnalyticsConfigDrawer = ({
  isOpen,
  onClose,
  config,
  onConfigChange,
  range,
  onRangeChange,
  filter,
  onFilterChange,
  runReportButtonLabel,
  isRequestCancelable,
  isRunReportButtonDisabled,
  onRunReportButtonClick,
}: AnalyticsConfigDrawerProps): JSX.Element | null => {
  const dialogRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const activeFiltersCount = (filter.all ?? []).length;

  // Focus into the dialog on open (the dialog itself, so the first Tab lands
  // on its first control), and keep the page behind from scrolling.
  useEffect(() => {
    if (!isOpen) return undefined;
    dialogRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Escape closes the drawer -- unless it was pressed in a combobox whose menu
  // is open (a filter's react-select: role="combobox" aria-expanded="true" on
  // its input), where it only closes that menu. (An expanded section header
  // is aria-expanded too, but has no popup to close.) Read in the CAPTURE phase, before
  // the control handles the key and its popup state changes. (The date
  // picker's popover handles its own Escape and stops it before it gets here.)
  const escapeOwnedByPopupRef = useRef(false);
  const handleKeyDownCapture = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== "Escape") return;
    const target = event.target;
    escapeOwnedByPopupRef.current =
      target instanceof HTMLElement &&
      target.getAttribute("role") === "combobox" &&
      target.getAttribute("aria-expanded") === "true";
  };

  // Tab / Shift+Tab wrap around inside the dialog.
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key === "Escape") {
      if (escapeOwnedByPopupRef.current) return;
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab" || !dialogRef.current) return;
    const focusable = [
      ...dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
    ].filter((el) => !el.closest("[hidden]"));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (typeof document === "undefined") return null;

  // Kept mounted while closed (hidden: out of the layout, the tab order and
  // the accessibility tree), so the filter controls keep what they hold beyond
  // the values -- the names of the selected campaigns, providers, cities --
  // across Run and reopen.
  return createPortal(
    <div className="fixed inset-0 z-[1400]" hidden={!isOpen}>
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close configuration"
        onClick={onClose}
        className="absolute inset-0 border-0 bg-[rgba(2,6,23,0.62)] p-0"
      />
      <aside
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDownCapture={handleKeyDownCapture}
        onKeyDown={handleKeyDown}
        className="absolute right-0 top-0 flex h-full w-full flex-col bg-(--surface-1) shadow-xl outline-none md:w-1/2 md:min-w-[560px]"
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-5">
          <div>
            <h2
              id={titleId}
              className="text-[1.05rem] font-semibold text-(--text-primary)"
            >
              Configure report
            </h2>
            <p className="mt-0.5 text-[12.5px] text-(--text-muted)">
              Group ad requests or responses by any set of properties over time.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-8 w-8 items-center justify-center rounded-(--radius-md) border-0 bg-transparent text-(--text-muted) transition hover:bg-(--surface-2) hover:text-(--text-primary)"
          >
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-4">
          {/* 1. Time frame + bucket */}
          <div className="flex flex-col gap-3 border-t border-(--border-subtle) py-4">
            <FieldLabel>Time frame</FieldLabel>
            <DateRangePicker
              value={range}
              onChange={onRangeChange}
              align="left"
            />
            <Segmented
              ariaLabel="Time bucket"
              value={config.bucket}
              options={BUCKETS}
              onChange={(bucket) => onConfigChange(withBucket(config, bucket))}
            />
          </div>

          {/* 2. Source */}
          <div className="flex flex-col gap-3 border-t border-(--border-subtle) py-4">
            <FieldLabel>Source</FieldLabel>
            <Segmented
              ariaLabel="Source"
              value={config.source}
              options={SOURCES}
              onChange={(source) => onConfigChange(withSource(config, source))}
            />
          </div>

          <CollapsibleSection
            title="Metrics"
            count={selectedMetricsCount(config)}
          >
            <MetricsSection config={config} onChange={onConfigChange} />
          </CollapsibleSection>

          <CollapsibleSection title="Group by" count={config.groupBy.length}>
            <GroupingsSection config={config} onChange={onConfigChange} />
          </CollapsibleSection>

          <CollapsibleSection
            title="Filters"
            count={activeFiltersCount}
            action={
              activeFiltersCount > 0 ? (
                <button
                  type="button"
                  onClick={() => onFilterChange({ all: [] })}
                  className="border-0 bg-transparent p-0 text-[12px] font-semibold text-(--text-muted) transition hover:text-(--text-primary)"
                >
                  Clear
                </button>
              ) : null
            }
          >
            <AnalyticsFiltersSection
              config={config}
              value={filter}
              onChange={onFilterChange}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title="Sort by"
            count={effectiveSortBy(config).length}
          >
            <SortSection config={config} onChange={onConfigChange} />
          </CollapsibleSection>

          <CollapsibleSection title="Columns" defaultOpen={false}>
            <ColumnsSection config={config} onChange={onConfigChange} />
          </CollapsibleSection>
        </div>

        <div className="border-t border-(--border-subtle) px-6 py-4">
          <button
            type="button"
            onClick={onRunReportButtonClick}
            disabled={isRunReportButtonDisabled}
            className={`inline-flex w-full items-center justify-center gap-2 rounded-(--radius-md) px-[18px] py-[10px] text-[13px] font-bold transition-colors ${
              isRequestCancelable
                ? "bg-(--status-danger) text-white hover:opacity-90"
                : isRunReportButtonDisabled
                  ? "cursor-not-allowed bg-(--brand-primary)/60 text-(--brand-primary-contrast)"
                  : "bg-(--brand-primary) text-(--brand-primary-contrast) hover:bg-(--brand-primary-hover)"
            }`}
          >
            {runReportButtonLabel}
          </button>
        </div>
      </aside>
    </div>,
    document.body,
  );
};
